import { HandySvg } from "handy-svg";
import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import iconPointerLeft from "../../assets/icons/svg-sprite/point-arrow-left.svg";
import iconShowMore from "../../assets/icons/svg-sprite/show-more.svg";
import iconCross from "../../assets/icons/svg-sprite/close-cross--small.svg";
import iconAttention from "../../assets/icons/svg-sprite/attention.svg";
import iconArrowRight from "../../assets/icons/svg-sprite/arrow-right.svg";
import { notification } from "../../lib/api/api";
import closeBanner from "../../modules/close-banner";

const NotificationContent = ({ message }) => {
  const [list, setList] = useState({ info: [], loading: false });
  const [limit, setLimit] = useState(10);
  const [categories, setCategories] = useState([]);
  const [filteredList, setFilteredList] = useState([]);

  const [needReadMap, setNeedReadMap] = useState({});
  const needReadMapRef = useRef({});
  const inFlightRef = useRef(new Set());

  const observerRef = useRef(null);
  const elementsRef = useRef(new Map());

  useEffect(() => {
    needReadMapRef.current = needReadMap;
  }, [needReadMap]);

  useEffect(() => {
    closeBanner();
    notification
      .notificationList()
      .then((res) => {
        const notify = res?.notify ?? [];
        setList({ info: notify, loading: true });
        setFilteredList(notify);
        setCategories(res?.categories ?? []);

        const initialMap = {};
        notify.forEach((n) => {
          if (n.is_read === 0) initialMap[n.id] = true;
        });
        setNeedReadMap(initialMap);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  const loadMoreData = useCallback(() => {
    setLimit((prevState) => prevState + 10);
  }, []);

  const notificationFilter = (categoryId) => {
    setLimit(10);
    setFilteredList(
      list.info.filter((item) => item.category_id === categoryId)
    );
  };

  function changeOfDate(date) {
    return `${date.slice(8, 10)}.${date.slice(5, 7)}.${date.slice(
      0,
      4
    )} в ${date.slice(11, 16)}`;
  }

  const onVisibleUnread = useCallback(async (id) => {
    // защита от дублей
    if (inFlightRef.current.has(id)) return;

    // запрос только если метка true (и уведомление реально непрочитано)
    if (!needReadMapRef.current[id]) return;

    inFlightRef.current.add(id);
    setNeedReadMap((prev) => ({ ...prev, [id]: false }));

    notification
      .readNotification(id)
      .then((res) => {
        if (res?.is_read === 1) {
          // обновляем is_read в обоих списках, чтобы убрать маркер
          setList((prev) => ({
            ...prev,
            info: prev.info.map((n) =>
              n.id === id ? { ...n, is_read: 1 } : n
            ),
          }));
          setFilteredList((prev) =>
            prev.map((n) => (n.id === id ? { ...n, is_read: 1 } : n))
          );

          // снимаем наблюдение с элемента
          const node = elementsRef.current.get(id);
          if (node && observerRef.current) observerRef.current.unobserve(node);
        } else {
          // если запрос неуспешен — вернем метку обратно
          setNeedReadMap((prev) => ({ ...prev, [id]: true }));
        }
      })
      .catch((err) => {
        setNeedReadMap((prev) => ({ ...prev, [id]: true }));
        console.log(err);
      })
      .finally(() => {
        inFlightRef.current.delete(id);
      });
  }, []);

  // создаем observer один раз
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const id = entry.target.dataset.id;
          const isRead = Number(entry.target.dataset.isread);

          if (isRead === 0) onVisibleUnread(id);
        });
      },
      {
        threshold: 0.1,
      }
    );

    elementsRef.current.forEach((node) => {
      observerRef.current.observe(node);
    });

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [onVisibleUnread]);

  // ref-коллбек для подписки/отписки элементов
  const setItemRef = useCallback((id) => {
    return (node) => {
      const obs = observerRef.current;
      const prevNode = elementsRef.current.get(id);

      if (prevNode && obs) obs.unobserve(prevNode);

      if (node) {
        elementsRef.current.set(id, node);
        if (obs) obs.observe(node);
      } else {
        elementsRef.current.delete(id);
      }
    };
  }, []);

  return (
    <>
      {!list.loading && <div className="preloader__wrapper" />}
      <div
        className="content__article-wrap"
        style={
          list.loading ? {} : { overflow: "hidden", height: "0px", margin: 0 }
        }
      >
        <div className="notifications">
          <div className="notifications__header page-container">
            <div className="notifications__return-button-wrap">
              <Link className="return-button" to="/">
                <HandySvg
                  className="svg-icon return-button__icon"
                  src={iconPointerLeft}
                  width="18"
                  height="10"
                />
                <span className="return-button__text">Вернуться</span>
              </Link>
            </div>
            <h2 className="notifications__title title title--h2">
              Все уведомления
            </h2>
          </div>
          <div className="notifications__main page-container">
            <div className="notifications__list">
              {filteredList.map(
                (item, index) =>
                  index < limit && (
                    <div
                      key={item.id}
                      ref={setItemRef(item.id)}
                      className="notifications__item"
                      data-id={item.id}
                      data-isread={item.is_read}
                    >
                      <div className="notifications__date">
                        {changeOfDate(item.date)}
                      </div>
                      <div style={{ position: "relative" }}>
                        {!item.is_read && (
                          <i className="notifications__status-mark" />
                        )}
                        {item.title.trim() !== "" && (
                          <div className="notifications__item-title">
                            {item.title}
                          </div>
                        )}
                        <div className="notifications__description">
                          {item.text}
                        </div>
                      </div>
                      {item.url.trim() !== "" && item.button.trim() !== "" && (
                        <Link
                          className="notifications__link arrow-link arrow-link--blue"
                          to={item.url}
                        >
                          {item.button}
                          <HandySvg
                            className="svg-icon"
                            src={iconArrowRight}
                            width="16"
                            height="16"
                          />
                        </Link>
                      )}
                    </div>
                  )
              )}
              {limit < filteredList.length && (
                <div className="notifications__btn-wrap">
                  <button
                    className="notifications__btn ghost-btn ghost-btn--blue bttn"
                    onClick={loadMoreData}
                    type="button"
                  >
                    <HandySvg
                      className="svg-icon notifications__btn-icon"
                      src={iconShowMore}
                      width="24"
                      height="24"
                    />
                    Показать еще
                  </button>
                </div>
              )}
              <div
                id="messageBanner"
                className="info-cards__message-container js-closing-banner is-active"
                style={{ display: "none" }}
              >
                <div className="info-cards__message">
                  <div className="info-cards__message-content">
                    <HandySvg
                      className="svg-icon"
                      src={iconAttention}
                      width="36"
                      height="36"
                    />
                    <div className="info-cards__message-text">{message}</div>
                  </div>
                  <button
                    className="js-close-btn info-cards__message-btn"
                    type="button"
                  >
                    <HandySvg
                      className="svg-icon"
                      src={iconCross}
                      width="13"
                      height="12"
                    />
                  </button>
                </div>
              </div>
            </div>
            <div className="notifications__aside-links">
              <ul className="aside-links">
                {categories.map((item) => (
                  <li key={item.id} className="aside-links__item">
                    <button
                      type="button"
                      className="aside-links__link"
                      style={{ width: "100%", background: "none" }}
                      onClick={() => notificationFilter(item.id)}
                    >
                      {item.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NotificationContent;
